<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import i18n, { getCurrentLocale } from '@/i18n';
import { useViewComposable } from '@/views/composable';
import {
  EMAIL_CONFIGURATION,
  EXPIRY_CONFIGURATION,
  FTV_ADMIN,
  FTV_CONFIGURATION,
  MARGIN_SAFETY,
  OPEN_JAW_CONFIGURATION,
  QUOTE_CONFIGURATION,
  RULE_ASSIGNMENT,
  VOUCHER_TEMPLATE,
  CALENDAR_CONFIGURATION,
  MAX_ROOM_CONFIGURATION,
  OPTION_CONFIGURATION,
  ADMIN_SETUP,
  MAX_ROOM
} from '@/constants';
import { getTextVariant } from '@/utils/stringUtils';
import { formatDate } from '@/utils/dateUtils';
import DataTable from 'primevue/datatable';
import TableColumn from 'primevue/column';
import ViewChangeDetail from '@/components/ruleHistory/ViewChangeDetail.vue';
import LongParagraphModal from '@/components/modal/utils/LongParagraphModal.vue';

interface Props {
  ruleHistory?: any;
  isTermConditionTypes?: boolean;
  groupTypes?: any;
  type?: string;
  destCode?: any;
}

const props = defineProps<Props>();
const { t } = useI18n({ useScope: 'global' });

const currentLanguage = getCurrentLocale();
const { renderQuoteType, departments, checkCalendarConfig } = useViewComposable();

const columns = ref([
  { field: 'rule', header: t('management.rule_history.rule') },
  { field: 'change', header: t('management.rule_history.action') },
  { field: 'fromTo', header: t('management.rule_history.change') },
  { field: 'user', header: t('management.rule_history.user') },
  { field: 'timestamp', header: t('management.rule_history.timestamp') }
]);

const rowsPerPageOptions = ref([10, 25, 50, 100, 200, 400, 500]);

const convertTimeStampToDate = (timestamp: string) => {
  return formatDate(timestamp, 'dd-LLL-yyyy HH:mm:ss', true, currentLanguage);
};

const convertType = (type: any) => {
  const typesCheck = [EMAIL_CONFIGURATION, EXPIRY_CONFIGURATION, MAX_ROOM_CONFIGURATION, OPEN_JAW_CONFIGURATION, RULE_ASSIGNMENT, FTV_CONFIGURATION, MARGIN_SAFETY, CALENDAR_CONFIGURATION, OPTION_CONFIGURATION];
  return typesCheck.includes(type) ? QUOTE_CONFIGURATION : type;
};

const handleRenderAction = (action: string) => {
  const getAction = action ?? 'Edit';
  const rule = i18n.global.messages.value.en.rule_history_log;
  const typeKey = convertType(props.type);
  if (getAction.toLowerCase() in rule[typeKey?.toLowerCase()]) {
    return t(`rule_history_log.${typeKey?.toLowerCase()}.${getAction.toLowerCase()}`);
  } else {
    return getAction;
  }
};

const getNameFromCode = (skString: string) => {
  const skArray = skString.split(', ').map((sk: string) => sk.trim());
  return props.groupTypes
    .filter((item: any) => skArray.includes(item.sk))
    .map((item: any) => getTextVariant(currentLanguage, item.name, item.nameFr))
    .join(', ');
};

const getCalendarRule = (data: any, ruleData: any, typeKey: string, ruleKey: string) => {
  const quoteKey = data.quoteType?.toLowerCase();
  const ruleTranslation = t(`rule_history_log.${typeKey}.${ruleKey}`);
  const quoteTranslation = quoteKey ? t(`rule_history_log.${typeKey}.${quoteKey}`) : '';
  if (checkCalendarConfig(ruleData)) {
    return `${ruleTranslation} - ${quoteTranslation} - ${getNameFromCode(data.extension)}`;
  } else {
    return `${ruleTranslation} - ${data.extension}`;
  }
};

const handleRenderRule = (ruleData: string, data: any) => {
  const rule = i18n.global.messages.value.en.rule_history_log;
  const typeKey = convertType(props.type)?.toLowerCase();
  const ruleKey = ruleData?.toLowerCase();
  const extensionKey = data.extension ? `${ruleKey}_${data.extension.toLowerCase()}` : null;

  if (!typeKey) {
    return ruleData;
  }

  const typeRule = rule[typeKey];
  if (data.extension) {
    if (extensionKey in typeRule) {
      return t(`rule_history_log.${typeKey}.${extensionKey}`);
    }
    if (ruleKey in typeRule) {
      return getCalendarRule(data, ruleData, typeKey, ruleKey);
    }
    if (props.type === OPTION_CONFIGURATION) {
      return data.extension;
    }
    return `${ruleKey} - ${data.extension}`;
  } else if (ruleKey in typeRule) {
    let ruleContent = t(`rule_history_log.${typeKey}.${ruleKey}`);
    if (ruleKey === 'max_rooms_config') {
      const destination = t(`rule_history_log.${typeKey}.max_rooms_config_destination`);
      const hotelCode = t(`rule_history_log.${typeKey}.max_rooms_config_hotel_code`);
      const roomCategory = t(`rule_history_log.${typeKey}.max_rooms_config_room_category`);
      const maxRooms = t(`rule_history_log.${typeKey}.max_rooms_config_max_rooms`);
      ruleContent = `${ruleContent} | ${destination} | ${hotelCode} | ${roomCategory} | ${maxRooms}`;
    }
    return ruleContent;
  }

  return ruleData;
};

const convertAutoAssign = (data: string) => {
  return departments.value.find((department: any) => department.value === data)?.label || data;
};

const handleUpdateRecord = (data: any) => {
  if (props.isTermConditionTypes) {
    return {
      ...data,
      rule: renderQuoteType(data.rule),
      change: handleRenderAction(data.change)
    };
  } else if (checkRuleAssignment(data.rule)) {
    return {
      ...data,
      from: convertAutoAssign(data.from),
      to: convertAutoAssign(data.to)
    };
  } else {
    return data;
  }
};

const checkRuleAssignment = (rule: string) => {
  return rule.toUpperCase().startsWith(RULE_ASSIGNMENT);
};

const checkRuleFTV = (rule: string) => {
  const ruleFTV = [VOUCHER_TEMPLATE, FTV_ADMIN, MAX_ROOM_CONFIGURATION];
  if (rule == MAX_ROOM) {
    return true;
  }
  return ruleFTV.some((ruleType) => rule.toUpperCase().startsWith(ruleType));
};

const formatRuleAssignment = (ruleAssignment: string, type: string) => {
  const formatRule = ruleAssignment.split('#');
  if (type === 'ruleOutcome') {
    return getTextVariant(currentLanguage, formatRule[1], formatRule[3]);
  } else {
    return getTextVariant(currentLanguage, formatRule[0], formatRule[2]);
  }
};

const formatRuleFTV = (data: any) => {
  return data.from || data.to ? `${t('worksheet.from')} "${data.from}" ${t('worksheet.to')} "${data.to}"` : 'EMPTY';
};

const checkAdminSetup = (type: any) => {
  if (type) {
    return ADMIN_SETUP.includes(type);
  }
};
</script>
<template>
  <div class="rule-history-table">
    <DataTable :value="ruleHistory" paginator :rows="rowsPerPageOptions[0]" :rowsPerPageOptions="rowsPerPageOptions" filterDisplay="menu" scrollHeight="900px" scrollable>
      <template #empty> {{ $t('management.no_data_found') }} </template>
      <TableColumn
        :show-filter-operator="false"
        :show-add-button="false"
        v-for="column of columns"
        :key="column.field"
        :field="column.field"
        :sortable="column.field === 'validateMessage'"
        :class="column.field === 'rule' && checkAdminSetup(props.type) && checkCalendarConfig(props.type) ? 'max-w-55' : ''"
      >
        <template #header>
          <span>
            {{ column.header }}
          </span>
        </template>
        <template #body="{ data, field }">
          <span v-if="field === 'fromTo'">
            <div v-if="!checkRuleFTV(data.rule)">
              <ViewChangeDetail :destCode="destCode" :data="handleUpdateRecord(data)" :isTermConditionTypes="isTermConditionTypes" :groupTypes="groupTypes" />
            </div>
            <div v-else>
              <span><LongParagraphModal :maxCharacters="67" class-component="break-word" :text="formatRuleFTV(data)" /></span>
            </div>
          </span>
          <span v-else-if="field === 'timestamp'">
            {{ convertTimeStampToDate(data.timestamp) }}
          </span>
          <span v-else-if="field === 'change'">
            {{ handleRenderAction(data.change) }}
          </span>
          <span v-else-if="field === 'rule'">
            <div v-if="!checkRuleAssignment(data.rule)">
              {{ isTermConditionTypes ? renderQuoteType(data.rule) : handleRenderRule(data.rule, data) }}
            </div>
            <div v-else>
              <div>{{ handleRenderAction(data.rule) }}</div>
              <div class="flex">
                <div>{{ $t('rule_assignment.rule') }}:</div>
                <div>
                  <span><LongParagraphModal :maxCharacters="67" class-component="break-word" :text="formatRuleAssignment(data.extension, 'rule')" /></span>
                </div>
              </div>
              <div>
                {{ $t('rule_assignment.rule_outcome') }}: <span>{{ formatRuleAssignment(data.extension, 'ruleOutcome') }}</span>
              </div>
            </div>
          </span>
          <span v-else>
            {{ data[field] }}
          </span>
        </template>
      </TableColumn>
    </DataTable>
  </div>
</template>
<style lang="scss">
.rule-history-table {
  .max-w-55 {
    width: 55rem;
  }
}
</style>
